import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../../../utils/api';

// Configure axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Add auth header interceptor
api.interceptors.request.use(async (config) => {
  const headers = await getAuthHeader(config.method);
  config.headers = { ...config.headers, ...Object.fromEntries(headers.entries()) };
  return config;
});

export function useAuctionHouses() {
  return useQuery({
    queryKey: ['auction-houses'],
    queryFn: async () => {
      const { data } = await api.get('/admin/auction-houses');
      return data;
    }
  });
}

export function useCreateAuctionHouse() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (auctionHouse) => {
      const { data } = await api.post('/admin/auction-houses', auctionHouse);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['auction-houses']);
    }
  });
}

export function useUpdateAuctionHouse() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...auctionHouse }) => {
      const { data } = await api.put(`/admin/auction-houses/${id}`, auctionHouse);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['auction-houses']);
    }
  });
}

export function useUploadAuctionHouseLogo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, formData }) => {
      const headers = await getAuthHeader('POST');
      // Remove content-type header as it will be set automatically for multipart/form-data
      headers.delete('Content-Type');
      
      try {
        // Use fetch instead of axios for better multipart handling
        const response = await fetch(`${API_BASE_URL}/admin/auction-houses/${id}/logo`, {
          method: 'POST',
          headers: headers,
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload logo');
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Logo upload error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['auction-houses']);
    }
  });
}

export function useDeleteAuctionHouse() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/admin/auction-houses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['auction-houses']);
    }
  });
}
